import { useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  detail?: string;
  link?: { label: string; href: string };
  duration?: number;
}

// Global toast state accessible from anywhere via store pattern
let toastSetters: Array<(toasts: Toast[]) => void> = [];
let globalToasts: Toast[] = [];

function emitToasts(toasts: Toast[]) {
  globalToasts = toasts;
  toastSetters.forEach((fn) => fn([...toasts]));
}

export function addGlobalToast(toast: Omit<Toast, 'id'>) {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const newToast: Toast = { duration: 5000, ...toast, id };
  const next = [newToast, ...globalToasts].slice(0, 5);
  emitToasts(next);

  if (newToast.duration && newToast.duration > 0) {
    setTimeout(() => {
      emitToasts(globalToasts.filter((t) => t.id !== id));
    }, newToast.duration);
  }
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>(globalToasts);

  // Register this component's setter
  useState(() => {
    toastSetters.push(setToasts);
    return () => {
      toastSetters = toastSetters.filter((fn) => fn !== setToasts);
    };
  });

  const dismiss = useCallback((id: string) => {
    emitToasts(globalToasts.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((type: ToastType, message: string, detail?: string, link?: Toast['link']) => {
    addGlobalToast({ type, message, detail, link });
  }, []);

  return { toasts, dismiss, toast };
}
